import { isUndefined, isNotNumber } from '../../utils/js.js';

/**
 * Funzione che converte una data in formato ISO tenendo contro del Timezone.
 */
const convertDateToISO = (date) => {
    if (isUndefined(date))
        return undefined;
    const myDate = date instanceof Date ? date : new Date(date);
    const newDate = myDate.setHours(myDate.getHours() - (myDate.getTimezoneOffset() / 60));
    return new Date(newDate).toISOString();
};
/**
 * Funzione che converte una data in formato DateOnly
 */
const convertDateToDateOnly = (date) => {
    if (isUndefined(date))
        return undefined;
    const myDate = date instanceof Date ? date : new Date(date);
    return `${myDate.getFullYear()}-${`00${myDate.getMonth() + 1}`.slice(-2)}-${`00${myDate.getDate()}`.slice(-2)}`;
};
/**
 * Verifica se la stringa in input può essere trasformata in una data
 * @param input Valore da analizzare
 * @param future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns data.
 */
function parseDate(input, future, decrease, annoDefault) {
    const parts = input.match(/(\d+)/g);
    if (!annoDefault) {
        if (parts == null || (parts.length !== 3 && parts.length !== 1)) {
            return null;
        }
    }
    else if (parts == null || (parts.length !== 3 && parts.length !== 2 && parts.length !== 1)) {
        return null;
    }
    let year;
    let month;
    let day;
    const anno = annoDefault ? parseInt(annoDefault, 10) : undefined;
    if (parts.length === 3 || (parts.length === 2 && annoDefault)) {
        year = anno || parseInt(parts[2], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[0], 10);
    }
    else {
        if (parts[0].length !== 6 && parts[0].length !== 8 && !annoDefault) {
            return null;
        }
        day = parseInt(parts[0].substring(0, 2), 10);
        month = parseInt(parts[0].substring(2, 4), 10);
        year = anno || parseInt(parts[0].substring(4), 10);
    }
    if (year < 100) {
        if (future) {
            year += 2000;
        }
        else {
            const d = new Date();
            if (year > d.getFullYear() % 100) {
                year += 1900;
            }
            else {
                year += 2000;
            }
        }
    }
    if (year < 1000 || year > 9999) {
        return null; // to avoid something like 202020 or 202
    }
    if (decrease) {
        month -= 1;
    }
    if (Object.prototype.toString.call(new Date(year, month - 1, day)) === '[object Date]') {
        return new Date(year, month, day);
    }
    return null;
}
/**
 * Verifica se la stringa in input può essere trasformata in una data
 * @param text testo
 * @param future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @returns data oppure null.
 */
function parseInputDate(text, future = true) {
    const parts = text.match(/(\d+)/g);
    if (parts == null || (parts.length !== 3 && parts.length !== 2 && parts.length !== 1)) {
        return null;
    }
    let year;
    let month;
    let day;
    if (parts.length === 3) {
        year = parseInt(parts[2], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[0], 10);
    }
    else {
        if (parts[0].length !== 4 && parts[0].length !== 6 && parts[0].length !== 8) {
            return null;
        }
        day = parseInt(parts[0].substring(0, 2), 10);
        month = parseInt(parts[0].substring(2, 4), 10);
        const annoStr = parts[0].substring(4);
        year = annoStr !== '' ? parseInt(parts[0].substring(4), 10) : new Date().getFullYear();
    }
    if (year < 100) {
        if (future) {
            year += 2000;
        }
        else {
            const d = new Date();
            if (year > d.getFullYear() % 100) {
                year += 1900;
            }
            else {
                year += 2000;
            }
        }
    }
    if (year < 1000 || year > 9999) {
        return null; // to avoid something like 202020 or 202
    }
    month -= 1;
    if (Object.prototype.toString.call(new Date(year, month - 1, day)) === '[object Date]') {
        return new Date(year, month, day);
    }
    return null;
}
/**
 * Verifica se la stringa in input può essere trasformata in una data
 * @param input Valore da analizzare
 * @param future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns data.
 */
function parseLegacyDate(input, future, decrease, annoDefault) {
    if (decrease === undefined && annoDefault === undefined) {
        // utilizzo la nuova gestione
        return parseInputDate(input, future);
    }
    return parseDate(input, future, decrease, annoDefault);
}
/**
 * Formatta la data secondo il formato dd/MM/yyyy
 */
const formatDateFromParts = (day, month, year, separator) => `${(`00${day}`).slice(-2)}${separator}${(`00${month}`).slice(-2)}${separator}${year}`;
/**
 * Formatta la data secondo il formato dd/MM/yyyy
 */
const formatDate = (date, separator = '/') => {
    const myDate = date instanceof Date ? date : new Date(date);
    const isInvaliDate = Number.isNaN(myDate.getTime());
    return isInvaliDate ? undefined : formatDateFromParts(myDate.getDate(), myDate.getMonth() + 1, myDate.getFullYear(), separator);
};
/**
 * Formatta la data secondo il formato MM/yyyy
 */
const formatMonth = (month, year, separator) => `${(`00${month}`).slice(-2)}${separator}${year}`;
/**
 * Verifica se la stringa in input può essere trasformata in un mese/anno
 * @param input Valore da analizzare
 * @param future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns data.
 */
const parseMonth = (input = '', future = true, decrease, annoDefault) => {
    const parts = input.match(/(\d+)/g);
    if (parts == null || (parts.length !== 2 && parts.length !== 1)) {
        return null;
    }
    let year;
    let month;
    if (parts.length === 2) {
        year = annoDefault ? parseInt(annoDefault, 10) : parseInt(parts[1], 10);
        month = parseInt(parts[0], 10);
    }
    else {
        if (parts[0].length !== 4 && parts[0].length !== 6 && !annoDefault) {
            return null;
        }
        month = parseInt(parts[0].substring(0, 2), 10);
        year = annoDefault ? parseInt(annoDefault, 10) : parseInt(parts[0].substring(2), 10);
    }
    if (year < 100) {
        if (future) {
            year += 2000;
        }
        else {
            const d = new Date();
            if (year > d.getFullYear() % 100) {
                year += 1900;
            }
            else {
                year += 2000;
            }
        }
    }
    if (year < 1000 || year > 9999) {
        return null; // to avoid something like 202020 or 202
    }
    if (decrease) {
        month -= 1;
    }
    return { year, month };
};
/**
 * Verifica se la stringa in input può essere trasformata in un mese/anno
 * @param  input Valore da analizzare
 * @param future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param  decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns data.
 */
// eslint-disable-next-line default-param-last
const parseMonthToDate = (input = '', future = true, decrease, annoDefault) => {
    const date = parseMonth(input, future, decrease, annoDefault);
    if (date)
        return new Date(date.year, date.month, 15);
    return null;
};
function getTimezoneOffset() {
    return new Date().getTimezoneOffset();
}
const pad = (number) => (Number(number) < 10 ? `0${Number(number)}` : number);
/**
 * Converte una data dal formato ISO a stringa
 * @param data stringa con data in formato ISO
 * @param time indica se necessaria anche il dato di HH:MM
 * @returns formato DD/MM/YYYY
 */
const parseIsoDateToString = (data, time = false) => {
    if (!data || typeof data !== 'string' || !data.match(/((\+|-)\d{2}:\d{2}|Z)$/gm))
        return '';
    const dataOraArray = data.split('T');
    const dataSplit = dataOraArray[0].split('-');
    const oraSplit = dataOraArray[1].split(':');
    const timeFormatted = time ? ` ${oraSplit[0]}:${oraSplit[1]}` : '';
    return `${pad(dataSplit[2])}/${pad(dataSplit[1])}/${dataSplit[0]}${timeFormatted}`;
};
/**
 * Converte una data dal formato ISO a Date
 * @param data stringa con data in formato ISO
 * @returns formato date
 */
const parseIsoDateToDate = (data) => {
    if (!data || typeof data !== 'string' || !data.match(/((\+|-)\d{2}:\d{2}|Z)$/gm))
        return null;
    return new Date(data);
};
/**
 * Converte una data dal formato dateOnly a stringa
 * @param data stringa con data in formato dateOnly
 * @returns formato DD/MM/YYYY
 */
const parseDateOnlyToString = (data) => {
    if (!data || typeof data !== 'string' || !data.match(/(\d{4}-\d{2}-\d{2})$/gm))
        return '';
    const dataSplit = data.split('-');
    return `${pad(dataSplit[2])}/${pad(dataSplit[1])}/${dataSplit[0]}`;
};
/**
 * Converte una data dal formato dateOnly a Date
 * @param data stringa con data in formato dateOnly
 * @returns formato date
 */
const parseDateOnlyToDate = (data) => {
    if (!data || typeof data !== 'string' || !data.match(/(\d{4}-\d{2}-\d{2})$/gm))
        return null;
    return new Date(data);
};
/**
 * Estra stringa che rappresenta il range di date.
 * @param from data di inizio
 * @param to data di fine
 * @param separator separatore
 * @returns stringa che rappresenta il range di date.
 */
const formatDateRange = (from, to, separator = '/') => {
    if (from !== null && to !== null) {
        const myFrom = from instanceof Date ? from : new Date(from);
        const myTo = to instanceof Date ? to : new Date(to);
        const dateFrom = myFrom instanceof Date ? formatDate(myFrom, separator) : undefined;
        const dateTo = myTo instanceof Date ? formatDate(myTo, separator) : undefined;
        if (dateFrom && dateTo) {
            return `${dateFrom} - ${dateTo}`;
        }
    }
    return '';
};
/**
 * Verifica se la stringa in input può essere trasformata in un range di date
 * @param input Valore da analizzare
 * @param future Indica se voglio inserire date nel futuro e quindi preinserire l'anno 2000 se indico la data con due cifre
 * @param decrease Indica se decrementare il mese(in quanto l'utente ad esempio per indicare gennaio indica 1 e non 0)
 * @param annoDefault Indica se settare un anno di default indipendentemente da quello digitato
 * @returns date o array con null.
 */
const parseDateRange = (input, future, decrease, annoDefault, spearator = '-') => {
    const [dataFrom, dataTo] = input.split(spearator).map((s) => s.trim());
    if (!dataFrom || !dataTo)
        return [undefined, undefined];
    return [
        parseDate(dataFrom, future, decrease, annoDefault),
        parseDate(dataTo, future, decrease, annoDefault),
    ];
};
/**
 * Verifica se la stringa in input può essere trasformata in un time HH:mm
 * @param input Valore da analizzare
 * @returns nuovo time formattato.
 */
const formatStringTime = (input) => {
    if (!input)
        return null;
    const [hourStr, minuteStr] = input.split(':').map((s) => s.trim());
    let hour = parseInt(hourStr, 10);
    let minute = parseInt(minuteStr, 10);
    if (isNotNumber(minute)) {
        if (hour < 100)
            return null;
        minute = hour % 100;
        hour = Math.floor(hour / 100);
    }
    if (hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
        return `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`;
    }
    return null;
};
const addDays = (date, days = 0) => {
    if (days === 0)
        return date;
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
};
function compareYMDDate(date1, date2) {
    const date1Value = (date1.getFullYear() * 10000) + (date1.getMonth() * 100) + date1.getDate();
    const date2Value = (date2.getFullYear() * 10000) + (date2.getMonth() * 100) + date2.getDate();
    return date1Value - date2Value;
}
const getPrevMonday = (date) => {
    if (date.getDay() === 0)
        return addDays(date, -6);
    return addDays(date, -1 * (date.getDay() - 1));
};
const addZero = (value) => (value < 10 ? `0${value}` : `${value}`);
/**
 * Formatta le ore, minuti di una data
 * @param data stringa con data in formato ISO
 * @param showSeconds boolean per permettere la visualizzazione anche dei secondi (default false)
 * @returns formato HH:mm[:ss]
 */
function formatTime(date, showSeconds = false) {
    const myDate = date instanceof Date ? date : new Date(date);
    const hour = addZero(myDate.getHours());
    const minutes = addZero(myDate.getMinutes());
    if (showSeconds) {
        const secs = addZero(myDate.getSeconds());
        return `${hour}:${minutes}:${secs}`;
    }
    return `${hour}:${minutes}`;
}
/**
 * Formatta un timestamp da una data
 * @param date stringa con data in formato ISO
 * @returns formato DD/MM/YYYY HH:mm:ss
 */
function formatTimeStamp(date) {
    const myDate = date instanceof Date ? date : new Date(date);
    return `${formatDate(myDate)} ${formatTime(myDate, true)}`;
}

export { addDays, compareYMDDate, convertDateToDateOnly, convertDateToISO, formatDate, formatDateFromParts, formatDateRange, formatMonth, formatStringTime, formatTime, formatTimeStamp, getPrevMonday, getTimezoneOffset, parseDate, parseDateOnlyToDate, parseDateOnlyToString, parseDateRange, parseInputDate, parseIsoDateToDate, parseIsoDateToString, parseLegacyDate, parseMonth, parseMonthToDate };
